<template>
  <div
    class="notes-app flex flex-col rounded-t-2 relative select-none"
    :class="{
      'fixed top-0 left-0 z-1000': fullscreen,
      'items-center justify-center': isPrivateNotesOn,
      'rounded-b-2': !notesEmbeddedAppUrl,
    }"
    :style="containerStyle"
  >
    <div
      v-if="isPrivateNotesOn"
      class="flex flex-col items-center justify-center gap-3"
      :style="{ color: themeVars.text3 }"
    >
      <CIcon icon="lock" size="20px" />
      <div class="text-center">
        {{ t('canvasUI.notesApp.privateNotesAreOpen') }}
      </div>
      <CButton v-if="showFocusNotes && !isPitcherWkWebView" text @click="emit('focusOpenedFullscreenNotes')">
        {{ t('canvasUI.notesApp.showPrivateNotes') }}
      </CButton>
    </div>
    <template v-else>
      <div v-if="!fullscreen" class="drag-handle flex items-center justify-center cursor-move">
        <CIcon icon="grip-dots" size="20px" />
      </div>
      <!-- Tabs (fullscreen only) placed above header/title -->
      <div v-else-if="isSpeakerNotesEnabled" class="px-4 border-b" :style="{ borderColor: themeVars.primary6 }">
        <div class="flex items-center gap-3 py-2">
          <!-- Meeting Notes first -->
          <CButton
            class="rounded-2 tracking-wider"
            :quaternary="activeTab !== 'my'"
            :strong="activeTab === 'my'"
            @click="activeTab = 'my'"
          >
            {{ t('canvasUI.notesApp.meetingNotes') }}
          </CButton>

          <!-- Speaker Notes second, disabled when not available -->
          <CButton
            class="rounded-2 tracking-wider"
            :disabled="!hasSpeakerNotes"
            :quaternary="activeTab !== 'speaker'"
            :strong="activeTab === 'speaker'"
            @click="activeTab = 'speaker'"
          >
            {{ t('canvasUI.notesApp.speakerNotes') }}
          </CButton>
        </div>
      </div>
      <div
        class="notes_app__header flex items-center justify-between px-4 pt-4 pb-2"
        :style="{ color: themeVars.primary }"
      >
        <div v-if="activeTab === 'my'" class="font-size-5 line-height-8 font-500">
          {{ t('canvasUI.notesApp.addNotes') }}
        </div>
        <div v-else-if="isSpeakerNotesEnabled && speakerTitle" class="font-size-5 line-height-8 font-500">
          {{ speakerTitle }}
        </div>

        <div v-if="!fullscreen" class="flex">
          <div
            v-if="!hideOpenInNewTab"
            class="h-9 w-9 cursor-pointer flex items-center justify-center mr-1"
            @click="emit('openInNewTab')"
          >
            <CIcon icon="arrow-up-right-from-square" size="16px" />
          </div>
          <div class="h-9 w-9 cursor-pointer flex items-center justify-center" @click="emit('close')">
            <CIcon icon="close" size="16px" />
          </div>
        </div>
        <div v-else-if="isPitcherWkWebView">
          <div class="h-9 w-9 cursor-pointer flex items-center justify-center" @click="emit('close')">
            <CIcon icon="close" size="16px" />
          </div>
        </div>
      </div>

      <!-- Speaker Notes content -->
      <div
        v-if="fullscreen && isSpeakerNotesEnabled && activeTab === 'speaker'"
        class="px-4 pb-2 flex-auto overflow-y-auto"
        @mousedown.stop
      >
        <template v-if="hasSpeakerNotes">
          <div
            v-if="speakerNotesHtml"
            class="mt-1 px-1 py-1 leading-relaxed speaker-notes-html"
            :style="{ color: themeVars.text2 }"
            v-html="speakerNotesHtml"
          ></div>
          <div v-else class="mt-1 px-1 py-1 whitespace-pre-wrap leading-relaxed" :style="{ color: themeVars.text2 }">
            {{ speakerNotes }}
          </div>
        </template>
        <template v-else>
          <div class="mt-1 px-3 py-8 rounded text-center" :style="{ color: themeVars.text3 }">
            {{ t('canvasUI.notesApp.noSpeakerNotesAvailable') }}
          </div>
        </template>
      </div>

      <div v-if="activeTab === 'my'" class="notes-text-area-container flex-auto flex" @mousedown.stop>
        <textarea
          id="notes-text-area"
          ref="notesTextAreaRef"
          v-model="content"
          class="w-full bg-transparent border-none pa-4 ma-0 outline-none resize-none font-inherit text-inherit shadow-none flex-auto"
          name="notes"
        ></textarea>
      </div>

      <CustomerReaction v-if="fullscreen && enableContentRatingInCall" class="mt-auto" />

      <div
        v-if="notesEmbeddedAppUrl && activeTab === 'my'"
        class="notes-text-area-container flex-auto pt-2"
        :style="{ borderTop: `1px solid ${themeVars.primary6}` }"
        @mousedown.stop
      >
        <iframe class="w-full h-full" frameborder="0" :src="notesEmbeddedAppUrl"></iframe>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed, nextTick, type CSSProperties, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocalStorage } from '@vueuse/core'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import useFullscreenNotesHeartbeat from '@notes/composables/useFullscreenNotesHeartbeat'
import useThemeVars from '@lib/composables/useThemeVars'
import { CallState, CALL_STORAGE_KEY, INITIAL_CALL_STATE, type Call } from '@lib/types/call'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import CustomerReaction from '@notes/components/CustomerReaction.vue'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { CanvasSection } from '@openapi'

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const enableContentRatingInCall = computed(
  () => pitcherSettings.value?.enable_content_rating_in_call && [CallState.IN_CALL].includes(call.value?.callState),
)
const isSpeakerNotesEnabled = computed(() => !!pitcherSettings.value?.enable_speaker_notes)

const emit = defineEmits<{
  openInNewTab: []
  close: []
  focusOpenedFullscreenNotes: []
}>()
const props = withDefaults(
  defineProps<{
    localStorageKey?: string
    localStorageAppUrlKey?: string // the extra embedded app url key
    fullscreen?: boolean
    isWidgetVisible?: boolean
    hideOpenInNewTab?: boolean
    showFocusNotes?: boolean
  }>(),
  {
    localStorageKey: 'notes_app_content',
    localStorageAppUrlKey: 'notes_app_url',
    fullscreen: false,
    isWidgetVisible: false,
    hideOpenInNewTab: false,
  },
)
const notesTextAreaRef = ref<HTMLTextAreaElement | null>(null)
const activeTab = ref<'my' | 'speaker'>('my')
const { t } = useI18n()
const { isSomeTabFullscreen } = useFullscreenNotesHeartbeat(props.fullscreen)
const themeVars = useThemeVars()
const content = useLocalStorage<string>(props.localStorageKey, '', {
  listenToStorageChanges: true,
})
const call = useLocalStorage<Call>(CALL_STORAGE_KEY, INITIAL_CALL_STATE, {
  deep: true,
  listenToStorageChanges: true,
})
const isPrivateNotesOn = computed(() => !props.fullscreen && isSomeTabFullscreen.value)
const containerStyle = computed(
  () =>
    ({
      background: themeVars.value.base,
      width: props.fullscreen ? '100%' : '300px',
      height: props.fullscreen ? '100vh' : '300px',
      border: props.fullscreen && isPitcherWkWebView ? `1px solid ${themeVars.value.primary6}` : 'none',
    }) satisfies CSSProperties,
)

// Active section metadata (kept in sync by canvas pages composable)
const activeSectionMetadata = useLocalStorage<CanvasSection['metadata'] | null>('active-section-metadata', null, {
  deep: true,
  listenToStorageChanges: true,
  serializer: {
    read: (v: string) => {
      try {
        const parsed = JSON.parse(v)

        return parsed ?? null
      } catch {
        return null
      }
    },
    write: (v: any) => JSON.stringify(v),
  },
})

const firstSlide = computed<any | null>(() => activeSectionMetadata.value?.pptx_data?.slides?.[0] ?? null)
const speakerTitle = computed<string | null>(() => (firstSlide.value?.title ? String(firstSlide.value.title) : null))

// Reactive reference for HTML speaker notes from localStorage
const htmlSpeakerNotesData = ref<{ html: string; text: string }>({ html: '', text: '' })

// Helper function to get HTML speaker notes from localStorage (pure function)
function getHtmlSpeakerNotes(): { html: string; text: string } {
  try {
    const htmlNotes = localStorage.getItem('speaker_notes_html')
    if (htmlNotes && htmlNotes.trim()) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlNotes
      return {
        html: htmlNotes.trim(),
        text: tempDiv.textContent?.trim() || '',
      }
    }
  } catch (e) {
    console.info('[NotesApp.vue]: could not read speaker notes from localStorage')
  }
  return { html: '', text: '' }
}

// Pure computed property for speaker notes sources
const speakerNotesSources = computed(() => {
  const direct = activeSectionMetadata.value?.speaker_notes
  const slideSpeaker = firstSlide.value?.speaker_notes
  const slideNote = firstSlide.value?.note

  return {
    direct: typeof direct === 'string' ? direct.trim() : '',
    slideSpeaker: typeof slideSpeaker === 'string' ? slideSpeaker.trim() : '',
    slideNote: typeof slideNote === 'string' ? slideNote.trim() : '',
  }
})

// Pure computed property for speaker notes text
const speakerNotes = computed<string>(() => {
  const sources = speakerNotesSources.value

  if (sources.direct) return sources.direct
  if (sources.slideSpeaker) return sources.slideSpeaker
  if (sources.slideNote) return sources.slideNote

  // Fallback to HTML notes text content
  return htmlSpeakerNotesData.value.text
})

// Computed property for HTML content
const speakerNotesHtml = computed<string>(() => {
  const sources = speakerNotesSources.value

  // Only use HTML if no other sources are available
  if (!sources.direct && !sources.slideSpeaker && !sources.slideNote) {
    return htmlSpeakerNotesData.value.html
  }

  return ''
})

// Watch for changes and update HTML speaker notes data
watch(
  speakerNotesSources,
  (sources) => {
    // Only fetch HTML notes if no other sources are available
    if (!sources.direct && !sources.slideSpeaker && !sources.slideNote) {
      htmlSpeakerNotesData.value = getHtmlSpeakerNotes()
    } else {
      htmlSpeakerNotesData.value = { html: '', text: '' }
    }
  },
  { immediate: true },
)

const hasSpeakerNotes = computed<boolean>(() => !!speakerNotes.value)

function maybeUpdateCallDescription(v: string = '') {
  try {
    if (!call.value || call.value.callState === CallState.NO_CALL) return
    call.value = {
      ...call.value,
      selectedEvent: {
        ...(call.value.selectedEvent ?? {}),
        Description: v,
      },
    }
  } catch (e) {
    console.info('[NotesApp.vue]: could not update call description in localStorage')
  }
}

function maybeHydrateFromCallDescription() {
  try {
    const call = localStorage.getItem('call')
    if (!call) return
    const meetingInfo = JSON.parse(call)
    if (!meetingInfo.selectedEvent) return
    content.value = meetingInfo.selectedEvent.Description
  } catch (e) {
    console.info('[NotesApp.vue]: could not hydrate notes from call description in localStorage')
  }
}

onMounted(async () => {
  notesTextAreaRef.value?.focus()
})

watch(
  () => props.isWidgetVisible,
  async (is, was) => {
    await nextTick()
    if (is && !was) {
      notesTextAreaRef.value?.focus()
    }
  },
)

watch(
  () => call.value?.callState,
  (callState, previousCallState) => {
    const isInCall = callState === CallState.IN_CALL
    const wasInCall = previousCallState === CallState.IN_CALL
    if (isInCall && !wasInCall) {
      maybeHydrateFromCallDescription()
    }
    const isNoCall = callState === CallState.NO_CALL
    const wasCallStopped = previousCallState === CallState.STOPPED
    if (isNoCall && wasCallStopped) {
      content.value = ''
    }
  },
)

watch(content, (c) => {
  c && maybeUpdateCallDescription(content.value)
})

// Updated to use the new localStorage parameter
const notesEmbeddedAppUrl = useLocalStorage<string>(props.localStorageAppUrlKey, '', {
  listenToStorageChanges: true,
})
</script>

<style lang="scss" scoped>
.notes_app__header {
  border-bottom: 1px solid v-bind('themeVars.primary6');
}

.speaker-notes-html {
  :deep(p) {
    margin: 0.5em 0;
  }
  :deep(ul),
  :deep(ol) {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }
  :deep(li) {
    margin: 0.25em 0;
  }
  :deep(strong),
  :deep(b) {
    font-weight: 600;
  }
  :deep(em),
  :deep(i) {
    font-style: italic;
  }
}
</style>
